export type RoutePoint = [longitude: number, latitude: number];

export interface RouteDefinition {
  name: string;
  points: RoutePoint[];
}

export interface StoredRoute extends RouteDefinition {
  id: string;
  createdAt: number;
  updatedAt: number;
}

const STORAGE_KEY = "klint.routes";
const SHARE_VERSION = 1;
const MAX_ROUTE_POINTS = 200;
const MAX_ROUTE_NAME_LENGTH = 80;
const EARTH_RADIUS_KM = 6371.0088;
const KM_PER_NAUTICAL_MILE = 1.852;

function validPoint(value: unknown): value is RoutePoint {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === "number" &&
    Number.isFinite(value[0]) &&
    value[0] >= -180 &&
    value[0] <= 180 &&
    typeof value[1] === "number" &&
    Number.isFinite(value[1]) &&
    value[1] >= -90 &&
    value[1] <= 90
  );
}

function validName(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.trim().length > 0 &&
    value.trim().length <= MAX_ROUTE_NAME_LENGTH
  );
}

function validDefinition(value: unknown): value is RouteDefinition {
  if (!value || typeof value !== "object") return false;
  const route = value as Partial<RouteDefinition>;
  return (
    validName(route.name) &&
    Array.isArray(route.points) &&
    route.points.length >= 2 &&
    route.points.length <= MAX_ROUTE_POINTS &&
    route.points.every(validPoint)
  );
}

function validStoredRoute(value: unknown): value is StoredRoute {
  if (!validDefinition(value)) return false;
  const route = value as Partial<StoredRoute>;
  return (
    typeof route.id === "string" &&
    route.id.length > 0 &&
    typeof route.createdAt === "number" &&
    Number.isFinite(route.createdAt) &&
    typeof route.updatedAt === "number" &&
    Number.isFinite(route.updatedAt)
  );
}

function writeRoutes(routes: StoredRoute[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(routes));
}

export function loadRoutes(): StoredRoute[] {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]") as unknown;
    if (!Array.isArray(value)) return [];
    return value
      .filter(validStoredRoute)
      .map((route) => ({
        ...route,
        name: route.name.trim(),
        points: route.points.map(
          ([longitude, latitude]): RoutePoint => [longitude, latitude],
        ),
      }))
      .sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    return [];
  }
}

function routeId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function saveRoute(
  definition: RouteDefinition,
  existingId: string | null = null,
): StoredRoute {
  if (!validDefinition(definition)) throw new Error("A route needs a name and at least two valid waypoints.");
  const routes = loadRoutes();
  const existing = existingId ? routes.find((route) => route.id === existingId) : null;
  const now = Date.now();
  const route: StoredRoute = {
    id: existing?.id ?? routeId(),
    name: definition.name.trim(),
    points: definition.points.map(([longitude, latitude]) => [longitude, latitude]),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
  writeRoutes([route, ...routes.filter((candidate) => candidate.id !== route.id)]);
  return route;
}

export function deleteRoute(id: string): void {
  writeRoutes(loadRoutes().filter((route) => route.id !== id));
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlDecode(value: string): Uint8Array {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) throw new Error("The route link is malformed.");
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  try {
    const binary = atob(padded);
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  } catch {
    throw new Error("The route link is malformed.");
  }
}

interface SharedRoutePayload {
  v: number;
  n: string;
  p: RoutePoint[];
}

export function routeUrl(definition: RouteDefinition): string {
  if (!validDefinition(definition)) throw new Error("Add at least two waypoints before sharing this route.");
  const payload: SharedRoutePayload = {
    v: SHARE_VERSION,
    n: definition.name.trim(),
    p: definition.points.map(([longitude, latitude]) => [
      Number(longitude.toFixed(6)),
      Number(latitude.toFixed(6)),
    ]),
  };
  const encoded = base64UrlEncode(new TextEncoder().encode(JSON.stringify(payload)));
  const url = new URL(document.baseURI);
  url.hash = `route=${encoded}`;
  return url.toString();
}

export function routeFromLocation(): RouteDefinition | null {
  const match = location.hash.match(/^#route=([A-Za-z0-9_-]+)$/);
  if (!match) return null;
  let payload: unknown;
  try {
    payload = JSON.parse(
      new TextDecoder("utf-8", { fatal: true }).decode(base64UrlDecode(match[1])),
    ) as unknown;
  } catch (error) {
    if (error instanceof Error && error.message === "The route link is malformed.") throw error;
    throw new Error("The route link contains invalid route data.");
  }
  if (!payload || typeof payload !== "object") {
    throw new Error("The route link contains invalid route data.");
  }
  const candidate = payload as Partial<SharedRoutePayload>;
  const definition = { name: candidate.n, points: candidate.p };
  if (candidate.v !== SHARE_VERSION || !validDefinition(definition)) {
    throw new Error("The route link contains an unsupported or invalid route.");
  }
  return {
    name: definition.name.trim(),
    points: definition.points.map(([longitude, latitude]) => [longitude, latitude]),
  };
}

function radians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

export function pointDistanceKm(a: RoutePoint, b: RoutePoint): number {
  const latitudeDelta = radians(b[1] - a[1]);
  const longitudeDelta = radians(b[0] - a[0]);
  const latitudeA = radians(a[1]);
  const latitudeB = radians(b[1]);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(latitudeA) * Math.cos(latitudeB) * Math.sin(longitudeDelta / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(haversine)));
}

export function routeDistanceKm(points: RoutePoint[]): number {
  let distance = 0;
  for (let index = 1; index < points.length; index++) {
    distance += pointDistanceKm(points[index - 1], points[index]);
  }
  return distance;
}

export function kilometresToNauticalMiles(kilometres: number): number {
  return kilometres / KM_PER_NAUTICAL_MILE;
}

/** Initial true bearing from one WGS84 point to another. */
export function bearingToPoint(from: RoutePoint, to: RoutePoint): number {
  const latitudeA = radians(from[1]);
  const latitudeB = radians(to[1]);
  const longitudeDelta = radians(to[0] - from[0]);
  const y = Math.sin(longitudeDelta) * Math.cos(latitudeB);
  const x =
    Math.cos(latitudeA) * Math.sin(latitudeB) -
    Math.sin(latitudeA) * Math.cos(latitudeB) * Math.cos(longitudeDelta);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}
